import React, { Component, PropTypes } from 'react';
import * as B from 'react-bootstrap';
import Errorable from 'components/lib/Errorable/';
import Loadable from 'components/lib/Loadable/';
import { CHAT_PART } from 'actions/chat';
import { WEB_RTC_FEATURES, WEB_RTC_CAMERA } from 'actions/chat/webrtc';
import { WEB_RTC_DEVICE_KIND } from 'reducers/chat/webrtc';
import VideoChat from './video';
import AudioChat from './audio';
import autobind from 'autobind-decorator';

@Errorable()
@Loadable({ size: 'medium' })
class ChatWebRTC extends Component {
  static propTypes = {
    height: PropTypes.number,
    mode: PropTypes.string,
    apiKey: PropTypes.string,
    sessionId: PropTypes.string,
    userToken: PropTypes.string,
    audioEstablishingLabel: PropTypes.string.isRequired,
    audioEstablishedLabel: PropTypes.string.isRequired,
    canPublishVideo: PropTypes.bool.isRequired,
    disableCameraSwitch: PropTypes.bool.isRequired,
    webRTCSubscriberConfiguration: PropTypes.object,
    webRTCPublisherConfiguration: PropTypes.object,
    devices: PropTypes.arrayOf(PropTypes.object),
    mainVideoDisplay: PropTypes.string,
    chosenCamera: PropTypes.string,
    preferedCamera: PropTypes.string,
    onClientError: PropTypes.func.isRequired,
    onEndWebRTC: PropTypes.func.isRequired
  };
  constructor(props) {
    super(props);

    this.state = {
      publisherStreaming: false,
      sessionStreaming: false,
    };
  }
  get sessionReadyToMount() {
    return this.props.apiKey && this.props.sessionId && this.props.userToken;
  }
  get wraperRendering() {
    return { height: this.props.height + 'px' };
  }
  get twoDisplays() {
    return this.props.mode === WEB_RTC_FEATURES.twoWayVideo && this.props.canPublishVideo;
  }
  get videoDevices() {
    return this.props.devices.filter(d => d.kind === 'videoInput');
  }
  get audioDevices() {
    return this.props.devices.filter(d => d.kind === 'audioInput');
  }
  get frontCamera() {
    return this.videoDevices.find(d => d.label.indexOf('front') !== -1);
  }
  get backCamera() {
    return this.videoDevices.find(d => d.label.indexOf('back') !== -1);
  }
  get hasTwoCameras() {
    return !!(this.frontCamera && this.backCamera);
  }
  get subscriberOptions() {
    return {
      ...this.props.webRTCSubscriberConfiguration,
      subscribeToVideo: this.props.mode === WEB_RTC_FEATURES.oneWayVideo
        || this.props.mode === WEB_RTC_FEATURES.twoWayVideo,
      subscribeToAudio: true,
    };
  }
  get publisherOptions() {
    let camera = this.props.chosenCamera || this.props.preferedCamera;

    return {
      ...this.props.webRTCPublisherConfiguration,
      videoSource: this.twoDisplays && this.hasTwoCameras ? (
        camera && (camera === WEB_RTC_CAMERA.frontal ? this.frontCamera : this.backCamera).deviceId
      ) : undefined,
      publishVideo: this.twoDisplays,
      publishAudio: true,
      resolution: (this.twoDisplays && VideoChat.videoResolution(this.wraper.offsetWidth, this.wraper.offsetHeight)) || undefined,
    };
  }
  invokeOnWebRTC(func) {
    func.bind(this);
  }
  errorFilter(continuation) {
    return err => {
      if (err) {
        this.props.onClientError(err);
      } else if (continuation) {
        return continuation();
      }
    }
  }
  errorHandler(err) {
    if (err) {
      this.props.onClientError(err);
    }
  }
  initPublisher(device) {
    let options = { ...this.publisherOptions };
    if (device) {
      options.videoSource = device;
    }
    this.publisher = OT.initPublisher('chat-webtrc-customer', options, this.errorHandler);
    this.publisher.once('streamCreated', ev => this.setState({ publisherStreaming: true }));
  }
  initSession(props) {
    props = props || this.props;
    this.session = OT.initSession(props.apiKey, props.sessionId);
    this.session.once('streamCreated', (ev) => {
      this.setState({ sessionStreaming: true });
      this.subscriber = this.session.subscribe(ev.stream, 'chat-webtrc-agent', this.subscriberOptions);
    });
    this.initPublisher();
    this.session.connect(props.userToken,
      this.errorFilter(() => this.session.publish(this.publisher, this.errorHandler))
    );
  }
  @autobind
  switchCamera() {
    this.publisher.cycleVideo();
  }
  @autobind
  ownScreenSnapshot() {
    if (this.publisher) {
      return this.publisher.getImgData();
    }
  }
  componentDidMount() {
    if (this.sessionReadyToMount) {
      this.initSession();
    }
  }
  componentWillReceiveProps(nextProps) {
    if (!this.session && nextProps.apiKey && nextProps.sessionId) {
      this.initSession(nextProps);
    }
  }
  componentWillUnmount() {
    if (this.session) {
      this.session.disconnect();
      delete this.session;
    }
    if (this.publisher) {
      this.publisher.destroy();
      delete this.publisher;
    }
  }
  render() {
    let wraperClassName = 'chat-webrtc-wraper',
        sharedComponentProps = {
          height: this.props.height,
          loading: this.props.loading,
          webRTCSubscriberConfiguration: this.props.webRTCSubscriberConfiguration,
          webRTCPublisherConfiguration: this.props.webRTCPublisherConfiguration,
          onClientError: this.props.onClientError,
          onEndWebRTC: this.props.onEndWebRTC,
          invokeOnWebRTC: this.invokeOnWebRTC.bind(this),
        };

    return (
      <div className={wraperClassName} ref={c => this.wraper = c} style={this.wraperRendering}>
        { this.props.mode === WEB_RTC_FEATURES.audioOnly ? undefined : this.loading() }
        { this.error() }
        {
          this.props.mode === WEB_RTC_FEATURES.audioOnly ? (
            <AudioChat { ...sharedComponentProps }
              audioEstablishingLabel={this.props.audioEstablishingLabel}
              audioEstablishedLabel={this.props.audioEstablishedLabel}
              established={this.state.publisherStreaming && this.state.sessionStreaming}
            />
          ) : (
            <VideoChat { ...sharedComponentProps }
              mode={this.props.mode}
              canPublishVideo={this.props.canPublishVideo}
              devices={this.props.devices}
              mainVideoDisplay={this.props.mainVideoDisplay}
              disableCameraSwitch={this.props.disableCameraSwitch}
              twoDisplays={this.twoDisplays}
              onSwitchCamera={this.switchCamera}
            />
        )
      }
      </div>
    );
  }
}

export default ChatWebRTC;



// WEBPACK FOOTER //
// ./components/chat/webrtc/index.js